import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { Plus, Save, Trash2, Edit2 } from "lucide-react";

const SERVICES = ["Wash & Fold", "Wash & Iron", "Dry Cleaning", "Steam Iron", "Shoe Cleaning"];

const Rates = () => {
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState({});
  const [newRate, setNewRate] = useState({ item: "", service: SERVICES[0], rate: 0 });
  const [q, setQ] = useState("");

  const load = () => api.get("/rates").then((r) => setRates(r.data));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const r = editing[id];
    await api.put(`/rates/${id}`, r);
    toast.success("Rate updated");
    setEditing((e) => { const { [id]: _, ...rest } = e; return rest; });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this rate?")) return;
    await api.delete(`/rates/${id}`);
    toast.success("Rate deleted");
    load();
  };
  const add = async () => {
    if (!newRate.item || newRate.rate <= 0) { toast.error("Enter item and rate"); return; }
    await api.post("/rates", newRate);
    toast.success("Rate added");
    setNewRate({ item: "", service: SERVICES[0], rate: 0 });
    load();
  };

  const filtered = rates.filter((r) => r.item.toLowerCase().includes(q.toLowerCase()) || r.service.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="rates-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Rate Management</h1>
        <p className="text-sm text-slate-500">Editable pricing for all services. Rates in ₹.</p>
      </div>

      <div className="qw-dash-card p-6" data-testid="rates-add-card">
        <div className="text-sm font-semibold text-slate-900 mb-3">Add New Rate</div>
        <div className="grid md:grid-cols-4 gap-3">
          <input placeholder="Item (e.g. Shirt)" className="qw-input" value={newRate.item} onChange={(e) => setNewRate({ ...newRate, item: e.target.value })} data-testid="rate-new-item"/>
          <select className="qw-input" value={newRate.service} onChange={(e) => setNewRate({ ...newRate, service: e.target.value })} data-testid="rate-new-service">
            {SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input type="number" placeholder="Rate ₹" className="qw-input" value={newRate.rate} onChange={(e) => setNewRate({ ...newRate, rate: Number(e.target.value) })} data-testid="rate-new-rate"/>
          <button onClick={add} className="qw-btn-primary" data-testid="rate-add-btn"><Plus size={16}/>Add Rate</button>
        </div>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item or service…" className="qw-input" data-testid="rates-search"/>

      <div className="qw-dash-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-500">
            <tr><th className="text-left p-4">Item</th><th className="text-left p-4">Service</th><th className="text-right p-4">Rate</th><th className="p-4"/></tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const e = editing[r.id];
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-4">{e ? <input className="qw-input" value={e.item} onChange={(x) => setEditing({ ...editing, [r.id]: { ...e, item: x.target.value }})}/> : <span className="font-medium">{r.item}</span>}</td>
                  <td className="p-4">{e ? (
                    <select className="qw-input" value={e.service} onChange={(x) => setEditing({ ...editing, [r.id]: { ...e, service: x.target.value }})}>
                      {SERVICES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  ) : <span className="qw-badge-slate">{r.service}</span>}</td>
                  <td className="p-4 text-right font-semibold">{e ? <input type="number" className="qw-input w-28 text-right ml-auto" value={e.rate} onChange={(x) => setEditing({ ...editing, [r.id]: { ...e, rate: Number(x.target.value) }})}/> : formatINR(r.rate)}</td>
                  <td className="p-4 text-right">
                    {e ? (
                      <button onClick={() => save(r.id)} className="qw-btn-primary text-sm py-1.5 px-3" data-testid={`rate-save-${r.id}`}><Save size={14}/>Save</button>
                    ) : (
                      <div className="inline-flex gap-2">
                        <button onClick={() => setEditing({ ...editing, [r.id]: { ...r }})} className="text-blue-600 hover:text-blue-800" data-testid={`rate-edit-${r.id}`}><Edit2 size={16}/></button>
                        <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700" data-testid={`rate-del-${r.id}`}><Trash2 size={16}/></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-slate-400">No rates match your search</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Rates;
